import React from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import Dialogue from '@app/component/overlays/dialog/Dialogue';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import Modal from '@app/component/overlays/modal/Modal';
import useModal from '@app/component/overlays/modal/ModalRef';
import FormButton from '@app/component/form/button/FormButton';
import { ExclamationTriangleIcon } from '@heroicons/react/16/solid';
import SubmitSelectivePricingInputAdjustmentType
	from '@app/model/pricing-automation/input/SubmitSelectivePricingInputAdjustmentType';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import SubmitMonthlyPricingResultOutputState
	from '@app/model/pricing-automation/output/SubmitMonthlyPricingResultOutputState';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import SubmitPricingResultOutput from '@app/model/pricing-automation/output/SubmitPricingResultOutput';
import PriceSubmissionBlockedItems from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/PriceSubmissionBlockedItems';
import Message from '@app/component/message/Message';
import { useTranslation } from '@app/component/translation/Translation';
import { useNavigate } from 'react-router-dom';
import BulkManualPricingFailureOutput from '@app/model/pricing-automation/output/BulkManualPricingFailureOutput';
import BulkAdjustmentBar from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/bulk-adjustment/BulkAdjustmentBar';
import {
	BulkAdjustmentValues,
	isBulkAdjustmentValueValid,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/bulk-adjustment/BulkAdjustment.utilities';

const MarketStateSnapshotSelectedRecommendationsBar: React.FunctionComponent<{
	selectedStatDates: string[],
	selectedRecommendationIds: number[],
	selectedDatesWithRecommendationCount: number,
	onBulkFailures: (failures: BulkManualPricingFailureOutput[]) => void,
	onClear: () => void
}> = ({ selectedStatDates, selectedRecommendationIds, selectedDatesWithRecommendationCount, onBulkFailures, onClear }) => {

	const { companyHotel } = useSelectedCompanyHotel();
	const queryClient = useQueryClient();
	const toaster = useToaster();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const submitDialogue = useDialogue();
	const manualOverrideModal = useModal();
	// The Manual Overwrite reads its mode and value from the shared Bulk Adjustment bar.
	const [manualAdjustment, setManualAdjustment] = React.useState<BulkAdjustmentValues>({ type: 'percentage', value: undefined });
	const [manualAdjustmentResetEpoch, setManualAdjustmentResetEpoch] = React.useState(0);
	const manualAdjustmentType = manualAdjustment.type === 'percentage'
		? SubmitSelectivePricingInputAdjustmentType.PERCENTAGE
		: SubmitSelectivePricingInputAdjustmentType.AMOUNT;
	const [submissionResult, setSubmissionResult] = React.useState<SubmitPricingResultOutput | null>(null);
	const selectedCount = selectedStatDates.length;
	const submissionError = submissionResult?.errorCode ?? submissionResult?.errorMessage;
	const noRecommendationsFound = submissionResult?.errorCode ===
		'server.exception.pricing_architecture.NO_ROOM_RECOMMENDATIONS_CREATED';

	const submitSelectivePricingMutation = useMutation({
		mutationFn: PricingAutomationService.submitSelectivePricing,
		onMutate: () => setSubmissionResult(null),
		onSuccess: async result => {
			if (result.state === SubmitMonthlyPricingResultOutputState.SUCCESS) {
				await queryClient.invalidateQueries({ queryKey: MarketStateGraphQLQueryKeys.all });
				toaster.success('Pricing', 'Selected price changes submitted successfully.');
				submitDialogue.close();
				onClear();
				return;
			}

			setSubmissionResult(result);
			submitDialogue.open();
		},
		onError: (error: string) => {
			toaster.error('Pricing', t(error));
		},
	});
	const submitBulkManualPricingMutation = useMutation({
		mutationFn: PricingAutomationService.submitBulkManualPricing,
		onMutate: () => onBulkFailures([]),
		onSuccess: async result => {
			if (result.state === SubmitMonthlyPricingResultOutputState.SUCCESS) {
				await queryClient.invalidateQueries({ queryKey: MarketStateGraphQLQueryKeys.all });
				toaster.success('Pricing', 'Manual price overwrite submitted successfully.');
				manualOverrideModal.close();
				onClear();
				return;
			}

			onBulkFailures(result.failures);
			manualOverrideModal.close();
			if (result.errorCode !== undefined && result.errorCode !== '') toaster.error('Pricing', t(result.errorCode));
		},
		onError: (error: string) => toaster.error('Pricing', t(error)),
	});
	const isSubmitting = submitSelectivePricingMutation.isPending || submitBulkManualPricingMutation.isPending;

	if (selectedCount === 0) return null;

	const submitSelectedPrices = () => {
		submitSelectivePricingMutation.mutate({
			companyHotelId: companyHotel.id,
			input: {
				recommendationIds: selectedRecommendationIds,
			},
		});
	};

	const submitManualOverwrite = () => {
		if (!isBulkAdjustmentValueValid(manualAdjustment.value)) return;
		const adjustment = manualAdjustment.value;
		submitBulkManualPricingMutation.mutate({
			companyHotelId: companyHotel.id,
			input: {
				statDates: selectedStatDates,
				adjustment,
				adjustmentType: manualAdjustmentType,
			},
		});
	};

	return <>
		<div className="fixed bottom-4 left-4 right-4 z-[9] md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2">
		<div className="relative grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-3 gap-y-3 rounded-xl border border-blue-200 bg-blue-100/95 px-4 py-3 shadow-lg ring-1 ring-blue-200 backdrop-blur md:inline-flex">
			<div className="col-span-3 pr-8 text-sm font-semibold text-gray-900 md:whitespace-nowrap md:pr-0">
				{selectedCount} {selectedCount === 1 ? 'date' : 'dates'} selected &middot; {selectedDatesWithRecommendationCount} with recommendation
			</div>
			<button type="button"
							onClick={() => submitDialogue.open()}
							disabled={isSubmitting || selectedRecommendationIds.length === 0}
							className="h-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-blue-700 hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:ring-gray-300 disabled:hover:bg-gray-200 md:h-auto">
				Submit
			</button>
			<span className="text-sm font-medium text-gray-500">OR</span>
			<button type="button"
							onClick={manualOverrideModal.open}
							disabled={isSubmitting}
							className="h-full rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-gray-300 hover:bg-gray-50 md:h-auto">
				Manual Overwrite
			</button>
			<button type="button"
							className="absolute right-3 top-3 rounded-md p-1 text-gray-400 hover:bg-blue-50 hover:text-gray-600 md:static"
							onClick={onClear}
							aria-label="Clear selected price changes">
				<XMarkIcon className="h-5 w-5" />
			</button>
		</div>
	</div>
		<Dialogue ref={submitDialogue.ref}
							title="Submit Selected Price Changes"
							confirmButtonName="Submit Prices"
							cancelButtonName="Cancel"
							isLoading={submitSelectivePricingMutation.isPending}
							confirmButtonDisabled={selectedRecommendationIds.length === 0 || noRecommendationsFound}
							isAutoClose={false}
							onConfirm={submitSelectedPrices}>
			<div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
				<div className="flex gap-2">
					<ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none text-amber-600" aria-hidden="true" />
					<p className="text-sm leading-5 text-amber-800">
						All valid recommendations on {selectedDatesWithRecommendationCount} selected {selectedDatesWithRecommendationCount === 1 ? 'date' : 'dates'} will be submitted.
						This action will push prices to the connected pricing channel.
					</p>
				</div>
			</div>
			{submissionResult !== null && submissionResult.blockedItems.length > 0 ? <PriceSubmissionBlockedItems
				items={submissionResult.blockedItems}
				currency={companyHotel.settings.main.currency}
				onDateSelect={selectedStatDate => {
					submitDialogue.close();
					navigate('/secured/rateshopper/rates/market/calendar', { state: { selectedStatDate } });
				}} /> : null}
			{submissionResult && submissionResult.blockedItems.length === 0 && submissionError !== undefined && submissionError !== '' &&
				<Message state="error">{t(submissionError, ...(submissionResult.errorParams ?? []))}</Message>}
		</Dialogue>
		<Modal
			ref={manualOverrideModal.ref}
			onOpen={() => setManualAdjustmentResetEpoch(epoch => epoch + 1)}
			title="Manual Overwrite"
			subtitle={`${selectedCount} ${selectedCount === 1 ? 'date' : 'dates'} selected`}
			actions={<FormButton
				state="primary"
				disabled={submitBulkManualPricingMutation.isPending || !isBulkAdjustmentValueValid(manualAdjustment.value)}
				isLoading={submitBulkManualPricingMutation.isPending}
				onClick={submitManualOverwrite}
			>
				Apply
			</FormButton>}
		>
			<div>
				<p className="mb-3 text-sm font-medium text-gray-700">Select how you want to apply the overwrite:</p>
				<BulkAdjustmentBar currency={companyHotel.settings.main.currency}
				label="Change every room by"
				showApply={false}
				applyOnEnter={false}
				layout="stack"
				resetEpoch={manualAdjustmentResetEpoch}
				onChange={setManualAdjustment} />
			</div>
		</Modal>
	</>;
};

export default MarketStateSnapshotSelectedRecommendationsBar;
